import React, { useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';

import { LbInput, LbSelect } from '@infoods/lifebase-ui';

import classes from './EditParticipantModal.module.scss';
import { LbModal } from '../../../../../../shared/components/modals/LbModal/LbModal';
import {
  ParticipantEditInfo,
  ParticipantRowInfo,
} from '../../../../../interfaces/participants-list/ParticipantRowInfo';
import { participantsListStore } from '../../../../../stores/participantsListStore';
import { apiStatusStore } from '../../../../../../shared/stores/apiStatusStore';
import { transformToOptions } from '../../../../../../shared/utils/arrayUtils';

type EditParticipantModalProps = {
  participant: ParticipantRowInfo;
  onClose: () => void;
};

type EditParticipantFormValues = {
  nickname: string;
  divisionId: number | '';
};

const EditParticipantModal = observer(
  ({ participant, onClose }: EditParticipantModalProps) => {
    const { isLoading } = apiStatusStore;
    const { updateParticipant, divisions } = participantsListStore;

    const { control, handleSubmit } = useForm<EditParticipantFormValues>({
      defaultValues: {
        nickname: participant.nickname || '',
        divisionId: participant.division?.id || '',
      },
    });

    const divisionOptions = useMemo(
      () =>
        transformToOptions({
          list: divisions,
          valueKey: 'id',
          labelKey: 'name',
        }),
      [divisions],
    );

    const onSubmit = handleSubmit((data) => {
      const editInfo: ParticipantEditInfo = {
        nickname: data.nickname,
        divisionId: data.divisionId || null,
      };

      updateParticipant(participant.id, editInfo);
      onClose();
    });

    const title = `Edit details about ${participant.firstName} ${participant.lastName}`;

    const generalInputProps = {
      control,
      size: 'small',
      variant: 'standard',
    } as const;

    return (
      <LbModal
        open
        title={title}
        isLoading={isLoading}
        primaryBtnConfig={{ onClick: onSubmit, text: 'Confirm' }}
        secondaryBtnConfig={{ onClick: onClose, text: 'Cancel' }}
      >
        <div className={classes.form}>
          <LbInput
            {...generalInputProps}
            name="nickname"
            label="Nickname"
            rules={{ required: 'Nickname is required' }}
          />
          {divisions.length > 0 && (
            <LbSelect
              {...generalInputProps}
              name="divisionId"
              label="Division"
              options={divisionOptions}
            />
          )}
        </div>
      </LbModal>
    );
  },
);

export { EditParticipantModal };
